import { apiClient } from './apiClient'
import { LoginRequest, LoginResponse, AuthUser, ApiResponse } from '@/types'

class AuthService {
  async login(credentials: LoginRequest): Promise<ApiResponse<LoginResponse>> {
    // Get the Supabase anon key from environment
    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY
    
    // Make the request with Authorization header
    return apiClient.post<LoginResponse>('/admin-auth-login', credentials, {
      'Authorization': `Bearer ${anonKey}`
    })
  }

  async logout(): Promise<ApiResponse<void>> {
    return { success: true, message: 'Logged out' }
  }

  async refreshToken(refreshToken: string): Promise<ApiResponse<LoginResponse>> {
    // Not implemented for admin
    return { success: false, message: 'Token refresh not supported' }
  }

  async getCurrentUser(): Promise<ApiResponse<AuthUser>> {
    // Not implemented for admin
    return { success: false, message: 'Get current user not supported' }
  }

  async changePassword(data: {
    current_password: string
    new_password: string
  }): Promise<ApiResponse<void>> {
    return apiClient.post<void>('/admin-auth-change-password', data)
  }

  async resetPassword(email: string): Promise<ApiResponse<void>> {
    return apiClient.post<void>('/admin-auth-reset-password', { email })
  }

  // Check if user has specific permission
  hasPermission(user: AuthUser | null, permission: string): boolean {
    if (!user) return false
    return user.permissions.includes(permission) || user.role === 'admin'
  }

  // Check if user has any of the specified permissions
  hasAnyPermission(user: AuthUser | null, permissions: string[]): boolean {
    if (!user) return false
    if (user.role === 'admin') return true
    return permissions.some(permission => user.permissions.includes(permission))
  }

  // Check if user has specific role
  hasRole(user: AuthUser | null, role: string): boolean {
    if (!user) return false
    return user.role === role
  }

  // Check if user has any of the specified roles
  hasAnyRole(user: AuthUser | null, roles: string[]): boolean {
    if (!user) return false
    return roles.includes(user.role)
  }
}

export const authService = new AuthService()

// Permission constants
export const PERMISSIONS = {
  // User management
  USERS_VIEW: 'users:view',
  USERS_CREATE: 'users:create',
  USERS_EDIT: 'users:edit',
  USERS_DELETE: 'users:delete',
  
  // Operator management
  OPERATORS_VIEW: 'operators:view',
  OPERATORS_CREATE: 'operators:create',
  OPERATORS_EDIT: 'operators:edit',
  OPERATORS_DELETE: 'operators:delete',
  
  // Withdrawal management
  WITHDRAWALS_VIEW: 'withdrawals:view',
  WITHDRAWALS_APPROVE: 'withdrawals:approve',
  WITHDRAWALS_REJECT: 'withdrawals:reject',
  
  // Financial operations
  FINANCE_VIEW: 'finance:view',
  FINANCE_APPROVE: 'finance:approve',
  
  // Audit operations
  AUDIT_VIEW: 'audit:view',
  AUDIT_APPROVE: 'audit:approve',
  
  // Reports
  REPORTS_VIEW: 'reports:view',
  REPORTS_EXPORT: 'reports:export',
  
  // System settings
  SETTINGS_VIEW: 'settings:view',
  SETTINGS_EDIT: 'settings:edit',
  
  // System logs
  LOGS_VIEW: 'logs:view',
} as const

export type Permission = typeof PERMISSIONS[keyof typeof PERMISSIONS]